/**
 * A quién le ofrecemos la web en otro idioma, y con qué palabras.
 *
 * La web sirve español por defecto y **no** autodetecta idioma
 * (`localeDetection: false` en `i18n/routing.ts`): redirigir por
 * `Accept-Language` mandaría a Googlebot —que rastrea desde IPs de EE. UU. y
 * sin cabecera de idioma— a `/en`, peleando con los canonical de las landings.
 * El `hreflang` ya coloca bien al tráfico que viene de búsqueda; lo que queda
 * descolocado es el directo, el compartido y el de campañas: un anglófono que
 * aterriza en la home en español sin saber que existe `/en`, y el caso inverso.
 *
 * De ahí un aviso sugerido en vez de una redirección. Este módulo es la parte
 * pura: qué idioma proponer y con qué copia. El DOM, la cookie de país y el
 * descarte viven en `components/locale-suggestion-banner.tsx`. Ver ADR-023.
 */

import type { PublicLocale } from '@/lib/locale-prefix'
import {
  getLocaleSuggestionMessages,
  type LocaleSuggestionMessages,
} from '@/lib/i18n/locale-messages/namespaces/locale-suggestion'

/**
 * Copia del aviso, **en el idioma que se sugiere**.
 *
 * No sale de `next-intl` a propósito: el mensaje lo lee precisamente quien no
 * entiende el locale activo, que es el único que el cliente tiene cargado.
 * «Esta página está disponible en inglés» no le sirve a un anglófono. Mismo
 * patrón que `components/embedded-browser-notice.tsx`, que necesita los tres
 * idiomas a la vez y los importa directo de `locales/`.
 *
 * El `Record<PublicLocale, …>` es además la fuente de verdad de qué idiomas
 * hablamos: añadir uno a `PublicLocale` rompe la compilación aquí, y con eso
 * `isSupported` se entera sin tocar nada.
 */
export const SUGGESTION_COPY: Record<PublicLocale, LocaleSuggestionMessages> = {
  es: getLocaleSuggestionMessages('es'),
  en: getLocaleSuggestionMessages('en'),
  pt: getLocaleSuggestionMessages('pt'),
  it: getLocaleSuggestionMessages('it'),
  fr: getLocaleSuggestionMessages('fr'),
  ja: getLocaleSuggestionMessages('ja'),
  de: getLocaleSuggestionMessages('de'),
}

/**
 * Subetiqueta de idioma de un BCP 47 (`pt-BR` → `pt`, `zh-Hant-TW` → `zh`).
 *
 * Acepta guion bajo además de guion: algunos WebViews emiten `pt_BR`. Devuelve
 * cadena vacía para entradas basura, que el llamante descarta.
 */
function baseLanguage(tag: string): string {
  return tag.trim().toLowerCase().split(/[-_]/)[0] ?? ''
}

function isSupported(language: string): language is PublicLocale {
  return language in SUGGESTION_COPY
}

/**
 * Idioma a sugerir, o `null` si no hay nada que ofrecer.
 *
 * La decisión tiene tres mitades, en este orden:
 *
 *  1. **¿Entiende la página?** Si el locale actual aparece en su lista —en
 *     cualquier posición, no solo la primera— no hay nada que avisar. Un
 *     hispanohablante con el navegador en inglés lee perfectamente la home.
 *  2. **¿Le contradice el país?** Si navega desde un país cuyo idioma de mercado
 *     es el de la página, tampoco. `navigator.languages` no es el idioma del
 *     sistema: es la lista «Idiomas preferidos» del navegador, que mucha gente
 *     nunca configura y que se queda en `en-US` de fábrica. Sin este contrapeso
 *     le ofrecíamos inglés a un padre en España leyendo la página en español.
 *     Ver `server/domain/market-language.ts` para por qué el mapa no cubre `en`.
 *  3. **¿Qué le ofrecemos?** El primer idioma de su lista que sepamos hablar.
 *     Si no hablamos ninguno, **nada**: no hay evidencia de que prefiera el
 *     inglés a la página en la que está, y adivinarlo sacaba un aviso en inglés
 *     a hablantes de catalán, gallego o euskera — el mercado principal.
 *
 * @param browserLanguages `navigator.languages`, en orden de preferencia.
 * @param currentLocale Locale de la página que se está viendo.
 * @param marketLanguage Idioma presupuesto por el país del edge, o `null`.
 */
export function pickSuggestedLocale(
  browserLanguages: readonly string[],
  currentLocale: PublicLocale,
  marketLanguage: PublicLocale | null
): PublicLocale | null {
  const preferences = browserLanguages.map(baseLanguage).filter((language) => language.length > 0)

  if (preferences.length === 0) return null
  if (preferences.includes(currentLocale)) return null
  if (marketLanguage === currentLocale) return null

  return preferences.find(isSupported) ?? null
}
